import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError, type AdminRole, type AdminUser } from '../lib/api'
import { fmtAgo, fmtFull } from '../lib/format'
import { useAuth } from '../lib/auth'
import { Card, Empty, Field, Loading, Modal, Pill, SecretReveal } from '../components/ui'

const ROLES: { value: AdminRole; label: string; blurb: string }[] = [
  { value: 'Viewer', label: 'Viewer', blurb: 'Read everything except secrets.' },
  {
    value: 'Operator',
    label: 'Operator',
    blurb: 'Approve devices, run diagnostics, acknowledge alerts, cancel messages.',
  },
  { value: 'Admin', label: 'Admin', blurb: 'Manage sources, API keys, devices, rules and settings.' },
  { value: 'Owner', label: 'Owner', blurb: 'Everything, including user accounts and device token revocation.' },
]

export default function Users() {
  const qc = useQueryClient()
  const { user: me } = useAuth()
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<AdminUser | null>(null)
  const [secret, setSecret] = useState<{ email: string; password: string } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const users = useQuery({ queryKey: ['users'], queryFn: () => api.get<AdminUser[]>('/api/admin/users') })

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['users'] })
  const onError = (err: unknown) => setError(err instanceof ApiError ? err.message : 'That did not work.')

  const suspend = useMutation({
    mutationFn: (id: string) => api.post(`/api/admin/users/${id}/suspend`),
    onSuccess: invalidate,
    onError,
  })
  const reactivate = useMutation({
    mutationFn: (id: string) => api.post(`/api/admin/users/${id}/reactivate`),
    onSuccess: invalidate,
    onError,
  })
  const reset = useMutation({
    mutationFn: (u: AdminUser) =>
      api
        .post<{ temporaryPassword: string }>(`/api/admin/users/${u.id}/reset-password`)
        .then((r) => ({ email: u.email, password: r.temporaryPassword })),
    onSuccess: (r) => {
      setSecret(r)
      invalidate()
    },
    onError,
  })

  return (
    <div className="stack" style={{ gap: 18 }}>
      <div className="page-head">
        <div>
          <h1>Users</h1>
          <div className="page-sub">
            Console accounts. Passwords are never stored or recoverable — resetting issues a temporary one the
            holder must replace at next sign-in.
          </div>
        </div>
        <button className="btn primary" onClick={() => setCreating(true)}>
          New user
        </button>
      </div>

      {error && (
        <div className="banner danger" onClick={() => setError(null)} style={{ cursor: 'pointer' }}>
          <div>{error}</div>
        </div>
      )}

      <Card flush>
        {users.isLoading ? (
          <Loading />
        ) : users.data?.length === 0 ? (
          <Empty title="No accounts" />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>2FA</th>
                  <th>Last sign-in</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {users.data?.map((u) => {
                  const isMe = u.id === me?.id
                  return (
                    <tr key={u.id}>
                      <td>
                        <strong>{u.displayName}</strong>
                        {isMe && <span className="chip" style={{ marginLeft: 7 }}>you</span>}
                        <div className="faint small mono">{u.email}</div>
                      </td>
                      <td>
                        <Pill tone={u.role === 'Owner' ? 'info' : 'idle'}>{u.role}</Pill>
                      </td>
                      <td>
                        {u.isActive ? (
                          u.mustChangePassword ? (
                            <Pill tone="warn">must change password</Pill>
                          ) : (
                            <Pill tone="ok">active</Pill>
                          )
                        ) : (
                          <Pill tone="danger">suspended</Pill>
                        )}
                      </td>
                      <td className="small">
                        {u.totpEnabled ? <span style={{ color: 'rgb(var(--ok))' }}>on</span> : <span className="faint">off</span>}
                      </td>
                      <td className="faint small" title={fmtFull(u.lastLoginAt)}>
                        {u.lastLoginAt ? fmtAgo(u.lastLoginAt) : 'never'}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <div className="btn-row" style={{ justifyContent: 'flex-end' }}>
                          <button className="btn sm" onClick={() => setEditing(u)}>
                            Edit
                          </button>
                          <button className="btn sm" onClick={() => reset.mutate(u)}>
                            Reset password
                          </button>
                          {u.isActive ? (
                            <button
                              className="btn danger sm"
                              onClick={() => suspend.mutate(u.id)}
                              disabled={isMe}
                              title={isMe ? 'You cannot suspend your own account' : undefined}
                            >
                              Suspend
                            </button>
                          ) : (
                            <button className="btn sm" onClick={() => reactivate.mutate(u.id)}>
                              Reactivate
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {creating && (
        <CreateUserModal
          onClose={() => setCreating(false)}
          onCreated={(email, password) => {
            setCreating(false)
            setSecret({ email, password })
            invalidate()
          }}
        />
      )}

      {editing && (
        <EditUserModal
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            invalidate()
          }}
        />
      )}

      {secret && (
        <Modal
          title="Temporary password"
          onClose={() => setSecret(null)}
          footer={
            <button className="btn primary" onClick={() => setSecret(null)}>
              Done
            </button>
          }
        >
          <div className="banner warn">
            <div>
              Hand this to <strong>{secret.email}</strong> over a channel you trust. They must change it at first
              sign-in. It is not stored and cannot be shown again.
            </div>
          </div>
          <SecretReveal label="Temporary password" value={secret.password} />
        </Modal>
      )}
    </div>
  )
}

function CreateUserModal({
  onClose,
  onCreated,
}: {
  onClose: () => void
  onCreated: (email: string, password: string) => void
}) {
  const [email, setEmail] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [role, setRole] = useState<AdminRole>('Operator')
  const [error, setError] = useState<string | null>(null)

  const create = useMutation({
    mutationFn: () =>
      api.post<{ temporaryPassword: string }>('/api/admin/users', { email, displayName, role }),
    onSuccess: (r) => onCreated(email, r.temporaryPassword),
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not create the account.'),
  })

  return (
    <Modal
      title="New user"
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn primary"
            onClick={() => create.mutate()}
            disabled={!email.includes('@') || !displayName.trim() || create.isPending}
          >
            {create.isPending ? 'Creating…' : 'Create user'}
          </button>
        </>
      }
    >
      {error && <div className="banner danger">{error}</div>}

      <Field label="Email">
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
      </Field>
      <Field label="Display name">
        <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
      </Field>
      <RolePicker role={role} onChange={setRole} />
    </Modal>
  )
}

function EditUserModal({
  user,
  onClose,
  onSaved,
}: {
  user: AdminUser
  onClose: () => void
  onSaved: () => void
}) {
  const [displayName, setDisplayName] = useState(user.displayName)
  const [role, setRole] = useState<AdminRole>(user.role)
  const [error, setError] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: () => api.put(`/api/admin/users/${user.id}`, { displayName, role }),
    onSuccess: onSaved,
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not save.'),
  })

  const clearTotp = useMutation({
    mutationFn: () => api.post(`/api/admin/users/${user.id}/clear-totp`),
    onSuccess: onSaved,
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not clear 2FA.'),
  })

  return (
    <Modal
      title={`Edit ${user.displayName}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {error && <div className="banner danger">{error}</div>}

      <Field label="Email" hint="Email cannot be changed. Create a new account instead.">
        <input value={user.email} disabled />
      </Field>
      <Field label="Display name">
        <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
      </Field>
      <RolePicker role={role} onChange={setRole} />

      {user.totpEnabled && (
        <>
          <div className="field-label" style={{ marginTop: 16 }}>
            Two-factor authentication
          </div>
          <div className="row between">
            <span className="muted small">Enrolled. Clear it if they have lost their device.</span>
            <button className="btn danger sm" onClick={() => clearTotp.mutate()}>
              Clear 2FA
            </button>
          </div>
        </>
      )}
    </Modal>
  )
}

function RolePicker({ role, onChange }: { role: AdminRole; onChange: (r: AdminRole) => void }) {
  return (
    <>
      <div className="field-label">Role</div>
      <div className="stack" style={{ gap: 7 }}>
        {ROLES.map((r) => (
          <label
            key={r.value}
            className="row"
            style={{
              gap: 10,
              padding: '9px 12px',
              border: '1px solid rgb(var(--line))',
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer',
              background: role === r.value ? 'rgb(var(--surface-2))' : undefined,
            }}
          >
            <input type="radio" checked={role === r.value} onChange={() => onChange(r.value)} />
            <div>
              <strong>{r.label}</strong>
              <div className="faint small">{r.blurb}</div>
            </div>
          </label>
        ))}
      </div>
    </>
  )
}
